import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import {
  ROW_HEIGHT,
  SERIES_ID_KEY,
  SERIES_NAME_KEY,
  X_AXIS_ID,
  Y_AXIS_ID,
} from './constants.js';
import { scrollContainer } from './TopList.sty.js';
import type { TopListProps } from './types/top-list.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import {
  coerceSizeValue,
  coerceSizeValueToPixels,
} from '../core/utils/coerceSizeValue.js';
import { DEFAULT_CHART_HEIGHT } from '../xy-chart/constants/defaults.js';
import type { XYChartData, XYChartRef } from '../xy-chart/types/xy-chart.js';
import { XYChart } from '../xy-chart/XYChartRoot.js';

export type { TopListProps };

const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'TopList';

/**
 * A horizontal ranked-bar chart with labels inside bars and a hidden numerical
 * axis, implemented as a wrapper around XYChart. When the list has more items
 * than fit in `height`, the viewport becomes scrollable.
 * @public
 */
export const TopList = forwardRef<XYChartRef, TopListProps>(function TopList(
  {
    data,
    labelAccessor,
    valueAccessor,
    categoryAccessor,
    categoryFormatter,
    valueFormatter,
    seriesIdAccessor,
    seriesNameAccessor,
    comparator,
    height = DEFAULT_CHART_HEIGHT,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': componentName = COMPONENT_NAME_BEHAVIOURAL_TRACKING,
    ...xyChartProps
  }: TopListProps,
  forwardedRef: ForwardedRef<XYChartRef>,
) {
  const internalRef = useRef<XYChartRef>(null);
  const mergedRef = useMergeRefs([internalRef, forwardedRef]);
  const onScroll = useCallback(() => internalRef.current?.reset(), []);

  const dataWithSyntheticSeries = useMemo(() => {
    const mapped =
      seriesIdAccessor !== undefined
        ? data
        : data.map((d) => ({
            ...d,
            [SERIES_ID_KEY]: 'series',
            [SERIES_NAME_KEY]: 'Series',
          }));

    const sortDescending = (a: XYChartData, b: XYChartData) =>
      Number(b[valueAccessor]) - Number(a[valueAccessor]);
    return [...mapped].sort(comparator ?? sortDescending);
  }, [comparator, data, seriesIdAccessor, valueAccessor]);

  const viewportPx = coerceSizeValueToPixels(height) ?? DEFAULT_CHART_HEIGHT;
  const contentHeight = Math.max(viewportPx, data.length * ROW_HEIGHT);

  const legend = seriesIdAccessor === undefined && <XYChart.Legend hidden />;

  return (
    <div
      className={scrollContainer}
      style={{ height: coerceSizeValue(height) }}
      onScroll={onScroll}
      data-dt-component={componentName}
    >
      <XYChart
        data={dataWithSyntheticSeries}
        height={contentHeight}
        ref={mergedRef}
        {...xyChartProps}
      >
        <XYChart.BarSeries
          variant="single"
          y0Accessor={categoryAccessor}
          x1Accessor={valueAccessor}
          labelAccessor={labelAccessor}
          seriesIdAccessor={seriesIdAccessor ?? SERIES_ID_KEY}
          nameAccessor={seriesNameAccessor ?? SERIES_NAME_KEY}
          yAxisId={Y_AXIS_ID}
          xAxisId={X_AXIS_ID}
        />
        <XYChart.XAxis
          id={X_AXIS_ID}
          type="numerical"
          position="bottom"
          hidden
          disableZoom
          disablePan
          formatter={valueFormatter}
        />
        <XYChart.YAxis
          id={Y_AXIS_ID}
          type="categorical"
          position="left"
          formatter={categoryFormatter}
        />
        {legend}
      </XYChart>
    </div>
  );
});
